import './styles.css';
import { calculateAnswerLayout } from './answer-layout.js';
import { buildReplyPool, drawMilkCards, EXAMPLE_CARD, resolveDrawPool, splitGraphemes } from './draw-engine.js';
import { planCanvasResize } from './canvas-resize.js';
import { buildSmoothSegment, buildStrokeTail } from './ink-path.js';
import { createMilkExport, dedupeMilk, mergeMilkData, parseMilkFile } from './milk-compat.js';
import {
  canStartPointer,
  collectPointerSamples,
  findTouchByIdentifier,
  resolveStrokeWidth,
  selectIOSTouch,
  shouldRecoverActivePointer,
} from './pointer-input.js';
import { loadState, resetToSamples, SAMPLE_CARDS, saveState } from './store.js';

const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

const icons = {
  eye: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z"/><circle cx="12" cy="12" r="2.5"/></svg>',
  eyeOff: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4l16 16M9.7 6.3A9.7 9.7 0 0 1 12 6c5.5 0 9 6 9 6a15 15 0 0 1-2.1 2.8M6.5 7.5C4.3 9.2 3 12 3 12s3.5 6 9 6c1 0 2-.2 2.8-.5"/></svg>',
  edit: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 16 10.5-10.5a2.1 2.1 0 0 1 3 0l1 1a2.1 2.1 0 0 1 0 3L8 20H4v-4Z"/><path d="m13 7 4 4"/></svg>',
  trash: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5"/></svg>',
};

const DISPLAY_TIMINGS = Object.freeze({
  slow: {
    idleDelay: 2200,
    fadeDuration: 950,
    answerDelay: 500,
    characterDelay: 130,
    cardDelay: 720,
    answerFadeDuration: 1100,
    fallbackDuration: 150,
  },
  normal: {
    idleDelay: 1500,
    fadeDuration: 650,
    answerDelay: 300,
    characterDelay: 70,
    cardDelay: 380,
    answerFadeDuration: 800,
    fallbackDuration: 85,
  },
  fast: {
    idleDelay: 850,
    fadeDuration: 360,
    answerDelay: 150,
    characterDelay: 28,
    cardDelay: 180,
    answerFadeDuration: 450,
    fallbackDuration: 45,
  },
});

let state = loadState();
let currentTab = 'cards';
let activeFilter = 'all';
let editingCardIndex = null;
let editingGroupIndex = null;
let pendingImport = null;

const canvas = $('#ink-canvas');
const answerLayer = $('#answer-layer');
const answerContent = $('#answer-content');
const drawer = $('#library-drawer');
const drawerBackdrop = $('#drawer-backdrop');

let context = canvas.getContext('2d');
let phase = 'idle';
let activePointerId = null;
let activePointerType = null;
let activeTouchIdentifier = null;
let activeStrokeRect = null;
let activeStrokeStarted = false;
let strokeContextSaved = false;
let previousPoint = null;
let lastRenderedPoint = null;
let activeStrokeWidth = 2.5;
let activeStrokeIsEraser = false;
let activeInkColor = '#252525';
let ignoreTouchUntil = 0;
let hasInk = false;
let idleTimer = null;
let answerTimer = null;
let transitionTimer = null;
let resizeTimer = null;
let pendingCanvasResize = false;
let animationRun = 0;
let previewRun = 0;
let lastAnswerCards = null;
let usingFullscreenFallback = false;
let canvasCssWidth = 0;
let canvasCssHeight = 0;
let canvasPixelRatio = 1;
const systemTheme = window.matchMedia('(prefers-color-scheme: dark)');
const isIOSLike = /iPad|iPhone|iPod/i.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

initialize();

function initialize() {
  applyTheme();
  setWritingViewportSize();
  window.requestAnimationFrame(() => document.body.classList.add('ui-ready'));
  bindCanvas();
  bindShellActions();
  bindDrawer();
  bindCardManagement();
  bindGroupManagement();
  bindSettings();
  bindDialogs();
  resizeCanvas(false);
  renderEverything();
  window.setTimeout(playSettingsPreview, 420);

  systemTheme.addEventListener?.('change', () => {
    if (state.settings.theme !== 'system') return;
    updateThemeColor();
    if (document.body.classList.contains('immersive-mode')) clearPage();
  });

  document.addEventListener('fullscreenchange', () => {
    if (document.fullscreenElement) {
      usingFullscreenFallback = false;
      setImmersiveMode(true);
    } else if (!usingFullscreenFallback) {
      setImmersiveMode(false);
    }
  });

  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (activePointerId !== null) {
        pendingCanvasResize = true;
        return;
      }
      if (!document.body.classList.contains('immersive-mode') || document.fullscreenElement) {
        setWritingViewportSize();
      }
      resizeCanvas(true);
      if (lastAnswerCards && answerLayer.classList.contains('is-visible')) {
        positionAnswer(lastAnswerCards);
      }
    }, 120);
  });

  window.addEventListener('orientationchange', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (activePointerId !== null) {
        pendingCanvasResize = true;
        return;
      }
      setWritingViewportSize();
      resizeCanvas(true);
      if (lastAnswerCards && answerLayer.classList.contains('is-visible')) {
        positionAnswer(lastAnswerCards);
      }
    }, 360);
  });

  if (/^\/manage\/?$/.test(window.location.pathname)) window.setTimeout(openDrawer, 0);

  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
  }
}

function bindCanvas() {
  if (isIOSLike) {
    // iPadOS Scribble can swallow rapid Apple Pencil PointerEvents. Touch
    // Events are a separate, reliable backend on iOS and expose touchType.
    canvas.addEventListener('touchstart', onIOSTouchStart, { passive: false });
    window.addEventListener('touchmove', onIOSTouchMove, { passive: false });
    window.addEventListener('touchend', onIOSTouchEnd, { capture: true, passive: false });
    window.addEventListener('touchcancel', onIOSTouchCancel, { capture: true, passive: false });
    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove, { passive: false });
    window.addEventListener('mouseup', onMouseUp, { capture: true, passive: false });
    document.addEventListener('gesturestart', preventIOSGesture, { passive: false });
    document.addEventListener('gesturechange', preventIOSGesture, { passive: false });
    document.addEventListener('gestureend', preventIOSGesture, { passive: false });
  } else {
    canvas.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp, { capture: true, passive: false });
    window.addEventListener('pointercancel', onPointerCancel, { capture: true, passive: false });
  }
  window.addEventListener('blur', () => finishActiveStroke({ commit: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) finishActiveStroke({ commit: true });
  });
  canvas.addEventListener('contextmenu', (event) => event.preventDefault());
  canvas.addEventListener('dblclick', (event) => event.preventDefault());
}

function preventIOSGesture(event) {
  if (document.body.classList.contains('immersive-mode')) event.preventDefault();
}

function onIOSTouchStart(event) {
  const touch = selectIOSTouch(event.changedTouches, { inputMode: state.settings.inputMode });
  if (!touch) {
    if (activeTouchIdentifier !== null || state.settings.inputMode === 'pencil') {
      if (event.cancelable) event.preventDefault();
    }
    return;
  }

  const pointerType = touch.touchType === 'stylus' ? 'pen' : 'touch';
  const now = performance.now();
  if (pointerType === 'touch' && now < ignoreTouchUntil) {
    if (event.cancelable) event.preventDefault();
    return;
  }

  if (activePointerId !== null) {
    if (pointerType !== 'pen') {
      if (event.cancelable) event.preventDefault();
      return;
    }
    // Pencil always wins over a palm/finger and also recovers a missed end.
    finishActiveStroke({ commit: false });
  }

  if (event.cancelable) event.preventDefault();
  const started = beginActiveStroke(touch, {
    pointerId: `ios-touch-${touch.identifier}`,
    pointerType,
  });
  if (started) activeTouchIdentifier = touch.identifier;
}

function onIOSTouchMove(event) {
  if (activeTouchIdentifier === null) return;
  const touch = findTouchByIdentifier(event.changedTouches, activeTouchIdentifier);
  if (!touch) return;
  if (event.cancelable) event.preventDefault();
  updateActiveStroke([touch]);
}

function onIOSTouchEnd(event) {
  if (activeTouchIdentifier === null) return;
  const touch = findTouchByIdentifier(event.changedTouches, activeTouchIdentifier);
  if (!touch) return;
  if (event.cancelable) event.preventDefault();
  finishActiveStroke({ event: touch, includeReleasePoint: true, commit: true });
}

function onIOSTouchCancel(event) {
  if (activeTouchIdentifier === null) return;
  const touch = findTouchByIdentifier(event.changedTouches, activeTouchIdentifier);
  if (!touch) return;
  if (event.cancelable) event.preventDefault();
  finishActiveStroke({ commit: true });
}

function onMouseDown(event) {
  const accepted = canStartPointer(event, {
    inputMode: state.settings.inputMode,
    activePointerType,
    ignoreTouchUntil,
    now: performance.now(),
  });
  if (!accepted) return;
  event.preventDefault();
  beginActiveStroke(event, { pointerId: 'mouse', pointerType: 'mouse' });
}

function onMouseMove(event) {
  if (activePointerId !== 'mouse' || phase !== 'drawing') return;
  if (Number(event.buttons) === 0) {
    finishActiveStroke({ commit: true });
    return;
  }
  event.preventDefault();
  updateActiveStroke([event]);
}

function onMouseUp(event) {
  if (activePointerId !== 'mouse') return;
  event.preventDefault();
  finishActiveStroke({ event, includeReleasePoint: true, commit: true });
}

function onPointerDown(event) {
  const now = performance.now();

  // A fresh pen-down proves the previous contact ended. Recover before the
  // admission check so one missed WebKit pointerup cannot reject every other
  // Apple Pencil stroke.
  if (shouldRecoverActivePointer(event, { activePointerId, activePointerType })) {
    finishActiveStroke({ commit: false });
  }

  const accepted = canStartPointer(event, {
    inputMode: state.settings.inputMode,
    activePointerType,
    ignoreTouchUntil,
    now,
  });
  if (!accepted) return;
  event.preventDefault();
  beginActiveStroke(event, {
    pointerId: event.pointerId,
    pointerType: event.pointerType || 'mouse',
    capturePointer: true,
  });
}

function beginActiveStroke(event, { pointerId, pointerType, capturePointer = false }) {
  if (phase === 'drinking') return false;
  if (phase === 'revealing' || phase === 'answer') clearPage(false);

  window.clearTimeout(idleTimer);
  phase = 'drawing';
  activePointerId = pointerId;
  activePointerType = pointerType;
  activeStrokeRect = canvas.getBoundingClientRect();
  activeStrokeStarted = activePointerType !== 'touch';
  if (capturePointer) {
    try {
      canvas.setPointerCapture?.(pointerId);
    } catch (_) {
      // Window listeners keep the stroke alive when capture is unavailable.
    }
  }
  previousPoint = pointFromEvent(event, activeStrokeRect);
  lastRenderedPoint = previousPoint;
  activeStrokeIsEraser = isEraserEvent(event);
  activeStrokeWidth = activeStrokeIsEraser ? 22 : lineWidthForEvent(event);
  activeInkColor = getInkColor();
  if (activePointerType === 'pen') ignoreTouchUntil = performance.now() + 900;

  context.save();
  strokeContextSaved = true;
  context.globalCompositeOperation = activeStrokeIsEraser ? 'destination-out' : 'source-over';
  context.fillStyle = activeStrokeIsEraser ? '#000' : activeInkColor;
  context.strokeStyle = activeInkColor;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  if (activeStrokeStarted) drawActiveStrokeDot(previousPoint, activeStrokeWidth);
  return true;
}

function onPointerMove(event) {
  if (event.pointerId !== activePointerId || phase !== 'drawing' || !previousPoint) return;
  if (activePointerType === 'pen' && Number(event.buttons) === 0) {
    finishActiveStroke({ commit: true });
    return;
  }
  event.preventDefault();
  if (activePointerType === 'pen') ignoreTouchUntil = performance.now() + 900;
  const events = collectPointerSamples(event, { useCoalesced: !isIOSLike });

  updateActiveStroke(events);
}

function updateActiveStroke(events) {
  if (phase !== 'drawing' || !previousPoint) return;
  if (activePointerType === 'pen') ignoreTouchUntil = performance.now() + 900;
  events.forEach((pointerEvent) => {
    const point = pointFromEvent(pointerEvent, activeStrokeRect);
    activeStrokeWidth = activeStrokeIsEraser ? 22 : lineWidthForEvent(pointerEvent);
    if (!activeStrokeStarted) {
      activeStrokeStarted = true;
      drawActiveStrokeDot(previousPoint, activeStrokeWidth);
    }
    const segment = buildSmoothSegment(lastRenderedPoint, previousPoint, point);
    drawInkSegment(segment, activeStrokeWidth);
    lastRenderedPoint = segment.nextRenderedPoint;
    previousPoint = segment.nextPoint;
    if (!activeStrokeIsEraser) hasInk = true;
  });
}

function onPointerUp(event) {
  if (event.pointerId !== activePointerId) return;
  event.preventDefault();
  finishActiveStroke({ event, includeReleasePoint: true, commit: true });
}

function onPointerCancel(event) {
  if (event.pointerId !== activePointerId) return;
  event.preventDefault();
  finishActiveStroke({ commit: true });
}

function finishActiveStroke({ event = null, includeReleasePoint = false, commit = false } = {}) {
  if (activePointerId === null) return;
  const wasPen = activePointerType === 'pen';

  if (includeReleasePoint && event && previousPoint) {
    const releasePoint = pointFromEvent(event, activeStrokeRect);
    if (Number.isFinite(releasePoint.x) && Number.isFinite(releasePoint.y)
      && Math.hypot(releasePoint.x - previousPoint.x, releasePoint.y - previousPoint.y) > .01) {
      if (!activeStrokeStarted) {
        activeStrokeStarted = true;
        drawActiveStrokeDot(previousPoint, activeStrokeWidth);
      }
      const segment = buildSmoothSegment(lastRenderedPoint, previousPoint, releasePoint);
      drawInkSegment(segment, activeStrokeWidth);
      lastRenderedPoint = segment.nextRenderedPoint;
      previousPoint = segment.nextPoint;
    }
  }

  if (!activeStrokeStarted && includeReleasePoint && previousPoint) {
    activeStrokeStarted = true;
    drawActiveStrokeDot(previousPoint, activeStrokeWidth);
  }
  if (activeStrokeStarted) {
    drawInkSegment(buildStrokeTail(lastRenderedPoint, previousPoint), activeStrokeWidth);
  }

  activePointerId = null;
  activePointerType = null;
  activeTouchIdentifier = null;
  activeStrokeRect = null;
  activeStrokeStarted = false;
  previousPoint = null;
  lastRenderedPoint = null;
  if (strokeContextSaved) {
    context.restore();
    strokeContextSaved = false;
  }

  if (wasPen) ignoreTouchUntil = performance.now() + 650;

  if (pendingCanvasResize) {
    pendingCanvasResize = false;
    setWritingViewportSize();
    resizeCanvas(true);
  }

  if (commit && hasInk) schedulePageCommit();
  else if (phase === 'drawing') phase = 'idle';
}

function drawActiveStrokeDot(point, width) {
  context.beginPath();
  context.arc(point.x, point.y, width / 2, 0, Math.PI * 2);
  context.fill();
  if (!activeStrokeIsEraser) {
    hasInk = true;
  }
}

function schedulePageCommit() {
  phase = 'waiting';
  window.clearTimeout(idleTimer);
  idleTimer = window.setTimeout(commitWrittenPage, getDisplayTiming().idleDelay);
}

function commitWrittenPage() {
  const drawn = drawMilkCards(resolveDrawPool(getActivePool()));
  const run = ++animationRun;
  const timing = getDisplayTiming();
  phase = 'drinking';
  canvas.style.setProperty('--ink-fade-duration', `${timing.fadeDuration}ms`);
  canvas.classList.add('is-drinking');

  transitionTimer = window.setTimeout(() => {
    if (run !== animationRun) return;
    clearCanvasPixels();
    canvas.classList.remove('is-drinking');
    transitionTimer = window.setTimeout(() => {
      if (run === animationRun) showAnswer(drawn, run);
    }, timing.answerDelay);
  }, timing.fadeDuration);
}

async function showAnswer(cards, run) {
  phase = 'revealing';
  hasInk = false;
  answerContent.replaceChildren();
  answerContent.className = 'answer-content';
  answerLayer.classList.remove('is-leaving');
  answerLayer.classList.add('is-visible');
  answerLayer.setAttribute('aria-hidden', 'false');
  const timing = getDisplayTiming();
  answerLayer.style.setProperty('--answer-fade-duration', `${timing.answerFadeDuration}ms`);
  lastAnswerCards = [...cards];
  positionAnswer(cards);
  const sequences = [];

  cards.forEach((card) => {
    const paragraph = document.createElement('p');
    paragraph.className = 'answer-card';
    const cardSequence = [];
    splitGraphemes(card).forEach((grapheme) => {
      if (grapheme === '\n') {
        const lineBreak = document.createElement('span');
        lineBreak.className = 'answer-line-break';
        paragraph.appendChild(lineBreak);
        return;
      }
      if (/\s/u.test(grapheme)) {
        const space = document.createElement('span');
        space.className = 'answer-space';
        paragraph.appendChild(space);
        return;
      }
      const glyph = makeAnswerGlyph(grapheme);
      paragraph.appendChild(glyph.element);
      cardSequence.push(glyph);
    });
    answerContent.appendChild(paragraph);
    sequences.push(cardSequence);
  });

  for (let cardIndex = 0; cardIndex < sequences.length; cardIndex += 1) {
    for (const glyph of sequences[cardIndex]) {
      if (run !== animationRun) return;
      await animateGlyph(glyph);
      if (run !== animationRun) return;
      await delay(Math.min(timing.fallbackDuration, timing.characterDelay));
    }
    if (cardIndex < sequences.length - 1) await delay(timing.cardDelay);
  }

  if (run !== animationRun) return;
  phase = 'answer';
  if (state.settings.answerHoldDuration > 0) {
    answerTimer = window.setTimeout(() => hideAnswer(run), state.settings.answerHoldDuration);
  }
}

function makeAnswerGlyph(grapheme) {
  const element = document.createElement('span');
  element.className = 'answer-glyph';
  if (/^[\p{Script=Latin}\p{Number}]$/u.test(grapheme)) element.classList.add('is-latin');
  if (/^[\p{Punctuation}\p{Symbol}]$/u.test(grapheme)) element.classList.add('is-punctuation');
  const fallback = document.createElement('span');
  fallback.className = 'answer-fallback';
  fallback.textContent = grapheme;
  fallback.style.setProperty('--fallback-duration', `${getDisplayTiming().fallbackDuration}ms`);
  element.appendChild(fallback);
  return { element, fallback };
}

async function animateGlyph(glyph) {
  const timing = getDisplayTiming();
  glyph.fallback.classList.add('is-visible');
  await delay(timing.fallbackDuration);
}

function hideAnswer(run) {
  if (run !== animationRun || !answerLayer.classList.contains('is-visible')) return;
  answerLayer.classList.add('is-leaving');
  transitionTimer = window.setTimeout(() => {
    if (run === animationRun) clearPage(false);
  }, getDisplayTiming().answerFadeDuration);
}

function clearPage() {
  finishActiveStroke({ commit: false });
  animationRun += 1;
  window.clearTimeout(idleTimer);
  window.clearTimeout(answerTimer);
  window.clearTimeout(transitionTimer);
  idleTimer = null;
  answerTimer = null;
  transitionTimer = null;
  phase = 'idle';
  hasInk = false;
  lastAnswerCards = null;
  canvas.classList.remove('is-drinking');
  clearCanvasPixels();
  answerLayer.classList.remove('is-visible', 'is-leaving');
  answerLayer.setAttribute('aria-hidden', 'true');
  answerContent.replaceChildren();
}

function drawInkSegment(segment, strokeWidth) {
  if (!segment) return;
  context.lineWidth = strokeWidth;
  context.beginPath();
  context.moveTo(segment.start.x, segment.start.y);
  context.quadraticCurveTo(segment.control.x, segment.control.y, segment.end.x, segment.end.y);
  context.stroke();
}

function positionAnswer(cards) {
  const viewport = canvas.getBoundingClientRect();
  const layout = calculateAnswerLayout({
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
    cardLengths: cards.map((card) => splitGraphemes(card).filter((item) => item !== '\n').length),
  });

  answerContent.style.setProperty('--character-size', `${layout.characterSize}px`);
  answerContent.style.setProperty('--card-visual-gap', `${Math.max(6, layout.characterSize * .24)}px`);
  answerContent.style.left = `${layout.left}px`;
  answerContent.style.top = `${layout.top}px`;
  answerContent.style.width = `${layout.targetWidth}px`;
  return layout.characterSize;
}

function delay(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, Math.max(0, milliseconds)));
}

function setWritingViewportSize() {
  const width = Math.max(1, Math.round(window.innerWidth || document.documentElement.clientWidth));
  const height = Math.max(1, Math.round(window.innerHeight || document.documentElement.clientHeight));
  document.documentElement.style.setProperty('--writing-viewport-width', `${width}px`);
  document.documentElement.style.setProperty('--writing-viewport-height', `${height}px`);
}

function resizeCanvas(preserve) {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
  const resizePlan = planCanvasResize({
    previousWidth: canvasCssWidth,
    previousHeight: canvasCssHeight,
    previousPixelRatio: canvasPixelRatio,
    nextWidth: rect.width,
    nextHeight: rect.height,
    nextPixelRatio: dpr,
  });
  if (!resizePlan.changed) return;
  const snapshot = document.createElement('canvas');
  if (preserve && canvas.width && canvas.height) {
    snapshot.width = canvas.width;
    snapshot.height = canvas.height;
    snapshot.getContext('2d').drawImage(canvas, 0, 0);
  }

  canvas.width = Math.round(rect.width * dpr);
  canvas.height = Math.round(rect.height * dpr);
  canvasCssWidth = rect.width;
  canvasCssHeight = rect.height;
  canvasPixelRatio = dpr;
  context = canvas.getContext('2d');
  context.setTransform(dpr, 0, 0, dpr, 0, 0);

  if (preserve && snapshot.width) {
    context.drawImage(
      snapshot,
      0,
      0,
      snapshot.width,
      snapshot.height,
      0,
      0,
      resizePlan.preservedWidth,
      resizePlan.preservedHeight,
    );
  }
}

function clearCanvasPixels() {
  const rect = canvas.getBoundingClientRect();
  context.clearRect(0, 0, rect.width, rect.height);
}

function pointFromEvent(event, rect = activeStrokeRect || canvas.getBoundingClientRect()) {
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

function lineWidthForEvent(event) {
  const pointerType = event.pointerType || activePointerType;
  return resolveStrokeWidth(event, { pointerType });
}

function isEraserEvent(event) {
  return event.button === 5 || Boolean(event.buttons & 32);
}

function getDisplayTiming() {
  return DISPLAY_TIMINGS[state.settings.displaySpeed] || DISPLAY_TIMINGS.normal;
}

function getInkColor() {
  return getComputedStyle(document.documentElement).getPropertyValue('--ink').trim() || '#252525';
}

function applyTheme() {
  const theme = state.settings.theme || 'system';
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme === 'system' ? 'light dark' : theme;
  updateThemeColor();
}

function updateThemeColor() {
  const dark = state.settings.theme === 'dark' || (state.settings.theme === 'system' && systemTheme.matches);
  $('meta[name="theme-color"]')?.setAttribute('content', dark ? '#151614' : '#efefec');
}

function getActivePool() {
  return buildReplyPool(state.customReplies, state.customReplyGroups, state.disabledReplyItems);
}

function bindShellActions() {
  $('#enter-writing').addEventListener('click', enterWritingMode);
  $('#home-import-cards').addEventListener('click', () => $('#file-input').click());
  $$('[data-home-tab]').forEach((button) => {
    button.addEventListener('click', () => {
      switchTab(button.dataset.homeTab);
      openDrawer();
    });
  });
  $$('[data-input-mode]').forEach((button) => {
    button.addEventListener('click', () => {
      state.settings.inputMode = button.dataset.inputMode;
      state = saveState(state);
      renderSettings();
    });
  });
  $$('[data-display-speed]').forEach((button) => {
    button.addEventListener('click', () => {
      state.settings.displaySpeed = button.dataset.displaySpeed;
      state = saveState(state);
      renderSettings();
      playSettingsPreview();
    });
  });
}

async function playSettingsPreview() {
  const target = $('#preview-answer');
  if (!target) return;
  const text = EXAMPLE_CARD;
  const timing = getDisplayTiming();
  const run = ++previewRun;
  target.replaceChildren();
  const glyphs = splitGraphemes(text).map((grapheme) => {
    const span = document.createElement('span');
    span.className = 'preview-glyph';
    span.style.setProperty('--preview-duration', `${timing.fallbackDuration}ms`);
    span.textContent = grapheme;
    target.appendChild(span);
    return span;
  });

  await delay(90);
  for (const glyph of glyphs) {
    if (run !== previewRun) return;
    glyph.classList.add('is-visible');
    await delay(timing.fallbackDuration + Math.min(timing.characterDelay, 80));
  }
}

async function enterWritingMode() {
  closeDrawer();
  previewRun += 1;
  clearPage();
  usingFullscreenFallback = false;
  if (document.documentElement.requestFullscreen) {
    try {
      await document.documentElement.requestFullscreen();
      setImmersiveMode(true);
      return;
    } catch (_) {
      // iOS 等环境可能不支持 Fullscreen API，下面使用站内沉浸模式。
    }
  }
  usingFullscreenFallback = true;
  setImmersiveMode(true);
}

async function exitWritingMode() {
  usingFullscreenFallback = false;
  if (document.fullscreenElement && document.exitFullscreen) {
    try {
      await document.exitFullscreen();
      return;
    } catch (_) {
      // 若浏览器拒绝退出，仍返回站内首页。
    }
  }
  setImmersiveMode(false);
}

function setImmersiveMode(enabled) {
  document.body.classList.toggle('immersive-mode', enabled);
  setWritingViewportSize();
  if (!enabled) clearPage();
  window.requestAnimationFrame(() => resizeCanvas(false));
}

function bindDrawer() {
  $('#close-library').addEventListener('click', closeDrawer);
  drawerBackdrop.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || $('.archive-dialog[open]')) return;
    if (document.body.classList.contains('immersive-mode')) {
      if (usingFullscreenFallback) exitWritingMode();
      return;
    }
    if (document.body.classList.contains('drawer-open')) closeDrawer();
    else openDrawer();
  });

  $$('.drawer-tab').forEach((button) => {
    button.addEventListener('click', () => switchTab(button.dataset.tab));
  });

  $('#card-search').addEventListener('input', renderCards);
  $('#group-filters').addEventListener('click', (event) => {
    const pill = event.target.closest('[data-filter]');
    if (!pill) return;
    activeFilter = pill.dataset.filter;
    renderGroupFilters();
    renderCards();
  });
}

function openDrawer() {
  if (document.body.classList.contains('immersive-mode')) return;
  document.body.classList.add('drawer-open');
  drawer.setAttribute('aria-hidden', 'false');
  renderEverything();
}

function closeDrawer() {
  document.body.classList.remove('drawer-open');
  drawer.setAttribute('aria-hidden', 'true');
  if (/^\/manage\/?$/.test(window.location.pathname)) window.history.replaceState(null, '', '/');
}

function switchTab(tab) {
  currentTab = tab;
  $$('.drawer-tab').forEach((button) => button.classList.toggle('is-active', button.dataset.tab === tab));
  $$('.drawer-panel').forEach((panel) => panel.classList.toggle('is-active', panel.dataset.panel === tab));
  if (tab === 'cards') renderCards();
  if (tab === 'groups') renderGroups();
}

function bindCardManagement() {
  $('#add-card').addEventListener('click', () => openCardDialog());
  $('#import-cards').addEventListener('click', () => $('#file-input').click());
  $('#export-cards').addEventListener('click', exportCards);
  $('#dedupe-cards').addEventListener('click', dedupeCards);
  $('#file-input').addEventListener('change', handleFileSelection);

  $('#card-list').addEventListener('click', (event) => {
    const action = event.target.closest('[data-card-action]');
    if (!action) return;
    const index = Number(action.dataset.index);
    if (!Number.isInteger(index) || !state.customReplies[index]) return;

    if (action.dataset.cardAction === 'toggle') toggleCard(index);
    if (action.dataset.cardAction === 'edit') openCardDialog(index);
    if (action.dataset.cardAction === 'delete') deleteCard(index);
  });
}

function renderEverything() {
  const pool = getActivePool();
  $('#home-active-count').textContent = pool.length;
  $('#home-group-count').textContent = state.customReplyGroups.length;
  $('#drawer-active-count').textContent = pool.length;
  $('#drawer-total-count').textContent = state.customReplies.length;
  document.title = 'Book字卡';
  renderGroupFilters();
  renderCards();
  renderGroups();
  renderSettings();
}

function renderGroupFilters() {
  const filters = [
    { id: 'all', name: '全部', color: '#9d8765' },
    { id: 'ungrouped', name: '未分组', color: '#6f6759' },
    ...state.customReplyGroups.map((group, index) => ({ id: `group-${index}`, name: group.name, color: group.color })),
  ];
  if (!filters.some((filter) => filter.id === activeFilter)) activeFilter = 'all';
  $('#group-filters').innerHTML = filters.map((filter) => `
    <button class="filter-pill ${activeFilter === filter.id ? 'is-active' : ''}" type="button" data-filter="${escapeHtml(filter.id)}" style="--pill-color:${escapeHtml(filter.color)}">
      <i></i>${escapeHtml(filter.name)}
    </button>
  `).join('');
}

function renderCards() {
  const query = $('#card-search').value.trim().toLocaleLowerCase('zh-CN');
  const cards = state.customReplies
    .map((text, index) => ({ text, index, group: findGroupForCard(text) }))
    .filter((entry) => {
      if (query && !entry.text.toLocaleLowerCase('zh-CN').includes(query)) return false;
      if (activeFilter === 'ungrouped' && entry.group) return false;
      if (activeFilter.startsWith('group-')) {
        const groupIndex = Number(activeFilter.slice(6));
        if (entry.group?.index !== groupIndex) return false;
      }
      return true;
    });

  const list = $('#card-list');
  if (!cards.length) {
    list.innerHTML = '<div class="empty-archive">这里没有找到字卡。<br>可以新增内容，或导入已有字卡。</div>';
    return;
  }

  const shown = cards.slice(0, 500);
  list.innerHTML = shown.map((entry, visibleIndex) => {
    const individuallyDisabled = state.disabledReplyItems.includes(entry.text);
    const groupDisabled = Boolean(entry.group?.group.disabled);
    const disabled = individuallyDisabled || groupDisabled;
    return `
      <article class="archive-card ${disabled ? 'is-disabled' : ''}">
        <span class="archive-card__index">${String(visibleIndex + 1).padStart(3, '0')}</span>
        <div class="archive-card__body">
          <p class="archive-card__text">${escapeHtml(entry.text)}</p>
          <div class="archive-card__meta">
            ${entry.group ? `<span class="archive-card__group" style="--group-color:${escapeHtml(entry.group.group.color)}"><i></i>${escapeHtml(entry.group.group.name)}</span>` : '<span>未分组</span>'}
            ${groupDisabled ? '<span>· 分组已停用</span>' : ''}
            ${individuallyDisabled ? '<span>· 单项已屏蔽</span>' : ''}
          </div>
        </div>
        <div class="archive-card__actions">
          <button class="mini-action ${individuallyDisabled ? 'is-active' : ''}" type="button" data-card-action="toggle" data-index="${entry.index}" aria-label="${individuallyDisabled ? '启用' : '屏蔽'}字卡">${individuallyDisabled ? icons.eyeOff : icons.eye}</button>
          <button class="mini-action" type="button" data-card-action="edit" data-index="${entry.index}" aria-label="编辑字卡">${icons.edit}</button>
          <button class="mini-action is-danger" type="button" data-card-action="delete" data-index="${entry.index}" aria-label="删除字卡">${icons.trash}</button>
        </div>
      </article>
    `;
  }).join('');

  if (cards.length > shown.length) {
    list.insertAdjacentHTML('beforeend', `<div class="empty-archive">字卡较多，为保持流畅仅显示前 ${shown.length} 条。<br>使用搜索可以定位其他内容。</div>`);
  }
}

function findGroupForCard(text) {
  const index = state.customReplyGroups.findIndex((group) => group.items?.includes(text));
  return index >= 0 ? { group: state.customReplyGroups[index], index } : null;
}

function toggleCard(index) {
  const text = state.customReplies[index];
  const disabled = new Set(state.disabledReplyItems);
  if (disabled.has(text)) disabled.delete(text);
  else disabled.add(text);
  state.disabledReplyItems = [...disabled];
  persistAndRender();
}

function deleteCard(index) {
  const text = state.customReplies[index];
  if (!window.confirm(`确定删除这张字卡吗？\n\n${text}`)) return;
  state.customReplies.splice(index, 1);
  state.customReplyGroups.forEach((group) => {
    group.items = (group.items || []).filter((item) => item !== text);
  });
  state.disabledReplyItems = state.disabledReplyItems.filter((item) => item !== text);
  persistAndRender();
  notify('字卡已从档案中移除。', 'success');
}

function openCardDialog(index = null) {
  editingCardIndex = index;
  const isEditing = Number.isInteger(index);
  $('#card-dialog-title').textContent = isEditing ? '编辑字卡' : '新增字卡';
  $('#card-content-label').textContent = isEditing ? '字卡内容' : '字卡内容 · 每行一张';
  $('#card-content').value = isEditing ? state.customReplies[index] : '';
  $('#card-content').rows = isEditing ? 5 : 7;
  fillGroupSelect($('#card-group'), isEditing ? findGroupForCard(state.customReplies[index])?.index : null);
  $('#card-dialog').showModal();
  window.setTimeout(() => $('#card-content').focus(), 50);
}

function fillGroupSelect(select, selectedIndex = null) {
  select.innerHTML = '<option value="">不分组</option>' + state.customReplyGroups
    .map((group, index) => `<option value="${index}" ${index === selectedIndex ? 'selected' : ''}>${escapeHtml(group.name)}${group.disabled ? '（已停用）' : ''}</option>`)
    .join('');
}

function saveCardForm() {
  const content = $('#card-content').value;
  const groupIndex = $('#card-group').value === '' ? null : Number($('#card-group').value);

  if (Number.isInteger(editingCardIndex)) {
    const nextText = content.trim();
    if (!nextText) return;
    const oldText = state.customReplies[editingCardIndex];
    state.customReplies[editingCardIndex] = nextText;
    state.customReplyGroups.forEach((group) => {
      group.items = (group.items || []).filter((item) => item !== oldText);
    });
    if (Number.isInteger(groupIndex) && state.customReplyGroups[groupIndex]) {
      state.customReplyGroups[groupIndex].items.push(nextText);
    }
    if (state.disabledReplyItems.includes(oldText)) {
      state.disabledReplyItems = state.disabledReplyItems.map((item) => item === oldText ? nextText : item);
    }
    notify('字卡内容已更新。', 'success');
  } else {
    const lines = content.split('\n').map((line) => line.trim()).filter(Boolean);
    const existing = new Set(state.customReplies.map(normalizeMilkString));
    const added = [];
    lines.forEach((line) => {
      const normalized = normalizeMilkString(line);
      if (!normalized || existing.has(normalized)) return;
      existing.add(normalized);
      state.customReplies.push(line);
      added.push(line);
    });
    if (Number.isInteger(groupIndex) && state.customReplyGroups[groupIndex]) {
      state.customReplyGroups[groupIndex].items.push(...added);
    }
    notify(`已收进 ${added.length} 张新字卡。`, 'success');
  }

  $('#card-dialog').close();
  persistAndRender();
}

function dedupeCards() {
  const before = state.customReplies.length;
  const { result, removedCount } = dedupeMilk(state.customReplies);
  if (!removedCount) {
    notify('没有发现重复字卡。');
    return;
  }

  const canonical = new Map(result.map((text) => [normalizeMilkString(text), text]));
  state.customReplies = result;
  state.customReplyGroups.forEach((group) => {
    group.items = [...new Set((group.items || []).map((item) => canonical.get(normalizeMilkString(item))).filter(Boolean))];
  });
  state.disabledReplyItems = [...new Set(state.disabledReplyItems.map((item) => canonical.get(normalizeMilkString(item))).filter(Boolean))];
  persistAndRender();
  notify(`已清理 ${before - state.customReplies.length} 张重复字卡。`, 'success');
}

async function handleFileSelection(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  try {
    pendingImport = await parseMilkFile(file);
    $('#import-summary').innerHTML = `
      <div class="import-summary__item"><strong>${pendingImport.customReplies.length}</strong><span>张字卡</span></div>
      <div class="import-summary__item"><strong>${pendingImport.customReplyGroups.length}</strong><span>个分组</span></div>
      <p class="import-summary__source">${escapeHtml(pendingImport.meta.format || 'milk 数据')} · ${escapeHtml(pendingImport.meta.fileName || file.name)}</p>
    `;
    $('#import-dialog').showModal();
  } catch (error) {
    pendingImport = null;
    notify(error.message || '导入文件无法读取。', 'error', 4600);
  }
}

function applyPendingImport() {
  if (!pendingImport) return;
  const mode = $('input[name="import-mode"]:checked')?.value || 'merge';
  state = mergeMilkData(state, pendingImport, mode);
  const count = pendingImport.customReplies.length;
  pendingImport = null;
  activeFilter = 'all';
  $('#card-search').value = '';
  $('#import-dialog').close();
  persistAndRender();
  notify(`字卡已${mode === 'overwrite' ? '覆盖' : '追加'}，读取 ${count} 张。`, 'success', 4200);
}

function exportCards() {
  const payload = createMilkExport(state);
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `reply-library-replies+groups-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
  notify('已导出兼容 milk 的字卡 JSON。', 'success');
}

function bindGroupManagement() {
  $('#add-group').addEventListener('click', () => openGroupDialog());
  $('#group-list').addEventListener('click', (event) => {
    const action = event.target.closest('[data-group-action]');
    if (!action) return;
    const index = Number(action.dataset.index);
    if (!state.customReplyGroups[index]) return;
    if (action.dataset.groupAction === 'toggle') {
      state.customReplyGroups[index].disabled = !state.customReplyGroups[index].disabled;
      persistAndRender();
    }
    if (action.dataset.groupAction === 'edit') openGroupDialog(index);
    if (action.dataset.groupAction === 'delete') deleteGroup(index);
  });
}

function renderGroups() {
  const list = $('#group-list');
  if (!state.customReplyGroups.length) {
    list.innerHTML = '<div class="empty-archive">还没有分组。<br>未分组字卡仍然会正常参与抽取。</div>';
    return;
  }

  list.innerHTML = state.customReplyGroups.map((group, index) => {
    const count = (group.items || []).filter((item) => state.customReplies.includes(item)).length;
    return `
      <article class="group-row ${group.disabled ? 'is-disabled' : ''}">
        <i class="group-swatch" style="--group-color:${escapeHtml(group.color)}"></i>
        <div class="group-row__body">
          <h3>${escapeHtml(group.name)}</h3>
          <div class="group-row__meta"><span>${count} 张字卡</span><span>· ${group.disabled ? '已退出卡池' : '参与抽取'}</span></div>
        </div>
        <div class="group-row__actions">
          <button class="mini-action ${group.disabled ? 'is-active' : ''}" type="button" data-group-action="toggle" data-index="${index}" aria-label="${group.disabled ? '启用' : '停用'}分组">${group.disabled ? icons.eyeOff : icons.eye}</button>
          <button class="mini-action" type="button" data-group-action="edit" data-index="${index}" aria-label="编辑分组">${icons.edit}</button>
          <button class="mini-action is-danger" type="button" data-group-action="delete" data-index="${index}" aria-label="删除分组">${icons.trash}</button>
        </div>
      </article>
    `;
  }).join('');
}

function openGroupDialog(index = null) {
  editingGroupIndex = index;
  const group = Number.isInteger(index) ? state.customReplyGroups[index] : null;
  $('#group-dialog-title').textContent = group ? '编辑分组' : '新建分组';
  $('#group-name').value = group?.name || '';
  $('#group-color').value = group?.color || randomGroupColor();
  $('#group-color-output').textContent = $('#group-color').value.toUpperCase();
  $('#group-disabled').checked = Boolean(group?.disabled);
  $('#group-dialog').showModal();
  window.setTimeout(() => $('#group-name').focus(), 50);
}

function saveGroupForm() {
  const name = $('#group-name').value.trim();
  if (!name) return;
  const color = $('#group-color').value;
  const disabled = $('#group-disabled').checked;

  if (Number.isInteger(editingGroupIndex)) {
    Object.assign(state.customReplyGroups[editingGroupIndex], { name, color, disabled });
    notify('分组已经更新。', 'success');
  } else {
    state.customReplyGroups.push({ id: Date.now(), name, color, disabled, items: [] });
    notify('新分组已经建立。', 'success');
  }
  $('#group-dialog').close();
  persistAndRender();
}

function deleteGroup(index) {
  const group = state.customReplyGroups[index];
  if (!window.confirm(`删除分组「${group.name}」？\n\n其中字卡不会被删除，将变为未分组。`)) return;
  state.customReplyGroups.splice(index, 1);
  if (activeFilter === `group-${index}`) activeFilter = 'all';
  persistAndRender();
  notify('分组已删除，字卡内容仍然保留。', 'success');
}

function randomGroupColor() {
  const colors = ['#8b5848', '#9a7048', '#66785e', '#596d78', '#765f82', '#876450', '#65705a'];
  return colors[Math.floor(Math.random() * colors.length)];
}

function bindSettings() {
  $('#setting-notebook-owner').addEventListener('input', (event) => {
    state.settings.notebookOwner = event.target.value;
    state = saveState(state);
    renderNotebookOwner();
  });
  $('#setting-notebook-owner').addEventListener('blur', (event) => {
    event.target.value = state.settings.notebookOwner;
  });
  $('#setting-input-mode').addEventListener('change', (event) => {
    state.settings.inputMode = event.target.value;
    state = saveState(state);
    renderSettings();
  });
  $('#setting-display-speed').addEventListener('change', (event) => {
    state.settings.displaySpeed = event.target.value;
    state = saveState(state);
    renderSettings();
    playSettingsPreview();
  });
  $('#setting-answer-hold').addEventListener('change', (event) => {
    state.settings.answerHoldDuration = Number(event.target.value);
    state = saveState(state);
  });
  $('#setting-theme').addEventListener('change', (event) => {
    state.settings.theme = event.target.value;
    state = saveState(state);
    applyTheme();
    clearPage();
  });
  $('#restore-samples').addEventListener('click', () => {
    if (!window.confirm('恢复示例字卡会替换当前字库与分组，确定继续吗？')) return;
    state = resetToSamples(state);
    persistAndRender();
    notify(`已经恢复 ${SAMPLE_CARDS.length} 张示例字卡。`, 'success');
  });
  $('#clear-library').addEventListener('click', () => {
    if (!window.confirm('确定清空全部字卡与分组吗？此操作无法撤销。')) return;
    state.customReplies = [];
    state.customReplyGroups = [];
    state.disabledReplyItems = [];
    clearPage();
    persistAndRender();
    notify('字库已经清空。');
  });
}

function renderSettings() {
  const ownerInput = $('#setting-notebook-owner');
  if (document.activeElement !== ownerInput) ownerInput.value = state.settings.notebookOwner;
  renderNotebookOwner();
  $('#setting-input-mode').value = state.settings.inputMode;
  $('#setting-display-speed').value = state.settings.displaySpeed;
  $('#setting-answer-hold').value = String(state.settings.answerHoldDuration);
  $('#setting-theme').value = state.settings.theme;
  $$('[data-input-mode]').forEach((button) => {
    const active = button.dataset.inputMode === state.settings.inputMode;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  });
  $$('[data-display-speed]').forEach((button) => {
    const active = button.dataset.displaySpeed === state.settings.displaySpeed;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  });
}

function renderNotebookOwner() {
  const owner = state.settings.notebookOwner;
  const label = $('#home-owner-label');
  label.textContent = owner ? `与 ${owner} 对话` : '';
  label.hidden = !owner;
}

function bindDialogs() {
  $$('.archive-dialog').forEach((dialog) => {
    $$('.dialog-close, .dialog-cancel', dialog).forEach((button) => button.addEventListener('click', () => dialog.close()));
    dialog.addEventListener('click', (event) => {
      const rect = dialog.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) dialog.close();
    });
  });

  $('#card-form').addEventListener('submit', (event) => {
    event.preventDefault();
    saveCardForm();
  });
  $('#group-form').addEventListener('submit', (event) => {
    event.preventDefault();
    saveGroupForm();
  });
  $('#import-form').addEventListener('submit', (event) => {
    event.preventDefault();
    applyPendingImport();
  });
  $('#group-color').addEventListener('input', () => {
    $('#group-color-output').textContent = $('#group-color').value.toUpperCase();
  });
}

function persistAndRender() {
  state = saveState(state);
  renderEverything();
}

function notify(message, type = 'info', duration = 3000) {
  const toast = document.createElement('div');
  toast.className = `toast ${type === 'error' ? 'is-error' : type === 'success' ? 'is-success' : ''}`;
  toast.textContent = message;
  $('#toast-region').appendChild(toast);
  window.setTimeout(() => {
    toast.classList.add('is-leaving');
    window.setTimeout(() => toast.remove(), 300);
  }, duration);
}

function normalizeMilkString(value) {
  return typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}
